import { useMemo } from 'react';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { calculateFlowDurationFromSlots, computeAllSlotTimings } from './slot-timing';

export type SlotFormState = {
  formInsertBeforeSlotId?: string;
  formInsertAfterSlotId?: string;
  formOffset: number;
  formDuration: number;
  formSlotType: 'Session' | 'Break';
  isUntimedFormActive: boolean;
  isFormActive: boolean;
};

export type SlotListComputed = {
  previewSlots: FlowSlotDto[];
  calculationSlots: FlowSlotDto[];
  displaySlots: FlowSlotDto[];
  timedSlots: FlowSlotDto[];
  untimedSlots: FlowSlotDto[];
  timedSlotsForDnd: FlowSlotDto[];
  untimedSlotsForDnd: FlowSlotDto[];
  timedSortableIds: string[];
  untimedSortableIds: string[];
  flowDuration: number | undefined;
  flowEndOffset: number | undefined;
};

const isUntimedSlot = (slot: FlowSlotDto) =>
  slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined);

type Params = {
  localSlots: FlowSlotDto[];
  formState: SlotFormState;
  previewFlow?: FlowDto;
  flow?: FlowDto;
  flowStartOffset: number;
  mode: 'PLAN' | 'ORGANIZE' | string;
};

export function useSlotListComputed({
  localSlots,
  formState,
  previewFlow,
  flow,
  flowStartOffset,
  mode,
}: Params): SlotListComputed {
  const {
    formInsertBeforeSlotId,
    formInsertAfterSlotId,
    formOffset,
    formDuration,
    formSlotType,
    isUntimedFormActive,
    isFormActive,
  } = formState;

  const previewSlots = useMemo(() => {
    if (!isFormActive || isUntimedFormActive) {
      return localSlots;
    }

    if (localSlots.length === 0) {
      return localSlots;
    }

    const tempSlotId = formInsertBeforeSlotId || formInsertAfterSlotId || `temp-${formOffset}`;
    const tempSlot: FlowSlotDto = {
      id: `temp-${tempSlotId}`,
      contentType: formSlotType,
      plannedDurationMinutes: formDuration,
      title: formSlotType === 'Break' ? 'New Break' : undefined,
      isUntimed: false,
      ...(formSlotType === 'Break' ? { content: undefined } : { content: undefined }),
    } as FlowSlotDto;

    let insertIndex: number;

    if (formInsertBeforeSlotId) {
      insertIndex = localSlots.findIndex((s) => s.id === formInsertBeforeSlotId);
      if (insertIndex === -1) insertIndex = localSlots.length;
    } else if (formInsertAfterSlotId) {
      insertIndex = localSlots.findIndex((s) => s.id === formInsertAfterSlotId);
      insertIndex = insertIndex === -1 ? localSlots.length : insertIndex + 1;
    } else {
      const flowToUse = previewFlow ?? flow;
      const currentDuration = flowToUse?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(localSlots);
      const computedSlots = computeAllSlotTimings(localSlots, currentDuration);

      insertIndex = computedSlots.findIndex((slot) => {
        const slotOffset = slot.computedOffsetMinutes ?? 0;
        return formOffset < slotOffset;
      });
      if (insertIndex === -1) insertIndex = localSlots.length;

      tempSlot.plannedOffsetMinutes = formOffset;
    }

    const slotsWithTemp = [...localSlots];
    slotsWithTemp.splice(insertIndex, 0, tempSlot);

    const flowToUse = previewFlow ?? flow;
    const durationToUse = flowToUse?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(slotsWithTemp);
    const computedSlots = computeAllSlotTimings(slotsWithTemp, durationToUse);
    return computedSlots;
  }, [
    flow,
    formDuration,
    formInsertAfterSlotId,
    formInsertBeforeSlotId,
    formOffset,
    formSlotType,
    isFormActive,
    isUntimedFormActive,
    localSlots,
    previewFlow,
  ]);

  const calculationSlots = useMemo(() => {
    return isFormActive && !isUntimedFormActive ? previewSlots : localSlots;
  }, [isFormActive, isUntimedFormActive, localSlots, previewSlots]);

  const displaySlots = useMemo(() => {
    return calculationSlots.filter((slot) => !slot.id.startsWith('temp-'));
  }, [calculationSlots]);

  const timedSlots = useMemo(() => {
    const filtered = displaySlots.filter((slot) => slot.contentType !== 'Gap' && !isUntimedSlot(slot));
    return filtered;
  }, [displaySlots]);

  const untimedSlots = useMemo(() => {
    return displaySlots.filter((slot) => slot.contentType !== 'Gap' && isUntimedSlot(slot));
  }, [displaySlots]);

  const flowDuration = useMemo(() => {
    const flowToUse = previewFlow ?? flow;
    const fixedDuration = flowToUse?.durationPlannedMinutes;
    if (fixedDuration !== undefined && fixedDuration > 0) {
      return fixedDuration;
    }

    // Only derive duration from the same timed session/break list as the UI (`timedSlots`), or from an
    // active timed create form (temp slot in `calculationSlots`). Do not fall back to raw `flow.slots`:
    // that can include Gap rows excluded from `timedSlots` and show a bogus total (e.g. end at 23:59).
    const slotsToUse =
      isFormActive && formDuration > 0
        ? calculationSlots.filter((slot) => slot.id.startsWith('temp-') || !isUntimedSlot(slot))
        : timedSlots;
    if (slotsToUse.length > 0) {
      return calculateFlowDurationFromSlots(slotsToUse);
    }

    return undefined;
  }, [calculationSlots, flow, formDuration, isFormActive, previewFlow, timedSlots]);

  const flowEndOffset = useMemo(() => {
    if (flowDuration !== undefined && flowDuration > 0) {
      return flowStartOffset + flowDuration;
    }

    if (timedSlots.length === 0) {
      return flowStartOffset;
    }

    const lastTimedSlot = timedSlots[timedSlots.length - 1];
    const lastSlotStart = lastTimedSlot.computedOffsetMinutes ?? 0;
    const lastSlotDuration = lastTimedSlot.id.startsWith('temp-')
      ? formDuration
      : (lastTimedSlot.plannedDurationMinutes ?? lastTimedSlot.computedDurationMinutes ?? 0);
    const lastSlotEnd = lastSlotStart + lastSlotDuration;
    return flowStartOffset + lastSlotEnd;
  }, [timedSlots, flowDuration, flowStartOffset, formDuration]);

  const timedSlotsForDnd = timedSlots;
  const untimedSlotsForDnd = untimedSlots;
  const timedSortableIds = useMemo(() => timedSlotsForDnd.map((slot) => slot.id), [timedSlotsForDnd]);
  const untimedSortableIds = useMemo(
    () => (mode !== 'PLAN' ? [] : untimedSlotsForDnd.map((slot) => slot.id)),
    [mode, untimedSlotsForDnd],
  );

  return {
    previewSlots,
    calculationSlots,
    displaySlots,
    timedSlots,
    untimedSlots,
    timedSlotsForDnd,
    untimedSlotsForDnd,
    timedSortableIds,
    untimedSortableIds,
    flowDuration,
    flowEndOffset,
  };
}

export default useSlotListComputed;
